import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client/react";
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  Eye,
  CheckCircle,
  XCircle,
  RefreshCw,
  Trash2,
} from "lucide-react";

import { GET_AI_LOGS } from "@/graphql/queries/admin";
import { DELETE_OLD_LOGS } from "@/graphql/mutations/admin";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const PAGE_SIZE = 20;

export default function LogsPage() {
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const { data, loading, refetch } = useQuery(GET_AI_LOGS, {
    variables: {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      status: statusFilter !== "all" ? statusFilter : undefined,
    },
  });

  const [deleteOldLogs, { loading: isDeleting }] =
    useMutation(DELETE_OLD_LOGS);

  const logs = data?.aiLogs?.logs ?? [];
  const total = data?.aiLogs?.total ?? 0;
  const stats = data?.aiLogs?.stats ?? null;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleDeleteOldLogs = async () => {
    try {
      const { data: deleteData } = await deleteOldLogs({
        variables: { daysToKeep: 30 },
      });
      toast.success(
        `Ištrinta ${deleteData?.deleteOldLogs?.deletedCount ?? 0} senų įrašų`
      );
      refetch();
    } catch {
      toast.error("Nepavyko ištrinti įrašų");
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("lt-LT", {
      dateStyle: "short",
      timeStyle: "medium",
    }).format(date);
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">AI žurnalai</h1>
          <p className="text-muted-foreground">
            Visų AI užklausų ir atsakymų istorija
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={loading}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
            />
            Atnaujinti
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" disabled={isDeleting}>
                <Trash2 className="h-4 w-4 mr-2" />
                Valyti senus
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Ištrinti senus įrašus?</AlertDialogTitle>
                <AlertDialogDescription>
                  Bus ištrinti visi AI žurnalo įrašai, senesni nei 30 dienų. Šio
                  veiksmo atšaukti nebegalėsite.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Atšaukti</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteOldLogs}>
                  Ištrinti
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Viso užklausų</div>
            <div className="text-2xl font-bold">{stats.totalCalls}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Sėkmingų</div>
            <div className="text-2xl font-bold text-green-600">
              {stats.successfulCalls}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Klaidų</div>
            <div className="text-2xl font-bold text-red-600">
              {stats.failedCalls}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Vid. trukmė</div>
            <div className="text-2xl font-bold">
              {formatDuration(stats.avgDurationMs)}
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filtrai</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="w-48">
              <Select
                value={statusFilter}
                onValueChange={(value) => {
                  setStatusFilter(value);
                  setPage(0);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Statusas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Visi</SelectItem>
                  <SelectItem value="success">Sėkmingi</SelectItem>
                  <SelectItem value="error">Klaidos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Užklausų sąrašas</CardTitle>
          <CardDescription>
            Rodoma {logs.length} iš {total} įrašų
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              Nėra AI žurnalo įrašų
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Operacija</TableHead>
                    <TableHead>Modelis</TableHead>
                    <TableHead>Raktas</TableHead>
                    <TableHead>Statusas</TableHead>
                    <TableHead>Trukmė</TableHead>
                    <TableHead>Tokenai</TableHead>
                    <TableHead className="text-right">Veiksmai</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log: any) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-mono text-sm">
                        {formatDate(log.createdAt)}
                      </TableCell>
                      <TableCell>{log.operation}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {log.model}
                      </TableCell>
                      <TableCell>
                        {log.keyType === "free" ? (
                          <Badge variant="outline" className="text-green-600 border-green-300 text-xs">
                            Free
                          </Badge>
                        ) : log.keyType === "paid" ? (
                          <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">
                            Paid
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {log.status === "success" ? (
                          <Badge variant="default" className="bg-green-500">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            OK
                          </Badge>
                        ) : (
                          <Badge variant="destructive">
                            <XCircle className="h-3 w-3 mr-1" />
                            Klaida
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{formatDuration(log.durationMs)}</TableCell>
                      <TableCell className="text-sm">
                        {log.promptTokens || log.responseTokens
                          ? `${log.promptTokens ?? 0} / ${log.responseTokens ?? 0}`
                          : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Link to={`/admin/logs/${log.id}`}>
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Puslapis {page + 1} iš {totalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Ankstesnis
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setPage((p) => Math.min(totalPages - 1, p + 1))
                      }
                      disabled={page >= totalPages - 1}
                    >
                      Kitas
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
